import React from "react";
import PropTypes from "prop-types";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  makeStyles,
} from "@material-ui/core";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";


const useStyles = makeStyles((theme) => ({
  card: {
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },

  header: {
    padding: theme.spacing(2, 2.5),
    backgroundColor: "#fafafa",
  },

  content: {
    padding: theme.spacing(2.5),
  },

  auctionBox: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    backgroundColor: "#fafafa",
  },

  stats: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },

  stat: {
    padding: "8px 14px",
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    minWidth: 110,
  },

  table: {
    minWidth: 850,
  },

  tableContainer: {
    maxHeight: 520,
    overflow: "auto",
  },

  actions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },

  button: {
    textTransform: "none",
    fontWeight: 600,
  },

  valid: {
    color: "#2e7d32",
    fontWeight: 600,
  },

  error: {
    color: "#d32f2f",
    fontWeight: 600,
  },

  lotText: {
    whiteSpace: "normal",
    wordBreak: "break-word",
  },

  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
  },
}));


const BulkInvoicePreview = ({
  file,
  rows,
  summary,
  selectedAuction,
  onBack,
  onGenerate,
}) => {

  const classes = useStyles();


  /*
   * ============================================================
   * SAFE VALUES
   * ============================================================
   */

  const safeRows =
    Array.isArray(rows)
      ? rows
      : [];


  const safeSummary = {
    total:
      Number(summary?.total || 0),

    valid:
      Number(summary?.valid || 0),

    errors:
      Number(summary?.errors || 0),
  };


  /*
   * ============================================================
   * CHECK IF CAN GENERATE
   * ============================================================
   */

  const canGenerate =
    !!selectedAuction &&
    safeRows.length > 0 &&
    safeSummary.errors === 0 &&
    safeSummary.valid > 0;


  return (
    <Card className={classes.card}>

      {/* ========================================================
          HEADER
      ======================================================== */}

      <CardHeader
        className={classes.header}

        title={
          <Typography variant="h6">
            Review Excel Data
          </Typography>
        }

        subheader={
          `STEP 2 — ${
            file
              ? file.name
              : "Excel file"
          }`
        }
      />

      <Divider />


      <CardContent className={classes.content}>


        {/* ======================================================
            SELECTED AUCTION
        ====================================================== */}

        <Box className={classes.auctionBox}>

          <Typography
            variant="body2"
            color="textSecondary"
          >
            Selected Auction
          </Typography>


          <Typography
            variant="h6"
          >
            {selectedAuction
              ? (
                selectedAuction.auction_name ||
                selectedAuction.name ||
                `Auction #${selectedAuction.id}`
              )
              : "---"}
          </Typography>


          {selectedAuction?.id && (
            <Typography
              variant="caption"
              color="textSecondary"
            >
              Auction ID: {selectedAuction.id}
            </Typography>
          )}

        </Box>


        {/* ======================================================
            SUMMARY
        ====================================================== */}

        <Box className={classes.stats}>

          {/* TOTAL */}

          <Box className={classes.stat}>

            <Typography variant="caption">
              TOTAL
            </Typography>

            <Typography variant="h6">
              {safeSummary.total}
            </Typography>

          </Box>


          {/* VALID */}

          <Box className={classes.stat}>

            <Typography variant="caption">
              VALID
            </Typography>

            <Typography
              variant="h6"
              className={classes.valid}
            >
              {safeSummary.valid}
            </Typography>

          </Box>


          {/* ERRORS */}

          <Box className={classes.stat}>

            <Typography variant="caption">
              ERRORS
            </Typography>

            <Typography
              variant="h6"
              className={classes.error}
            >
              {safeSummary.errors}
            </Typography>

          </Box>

        </Box>


        {/* ======================================================
            TABLE
        ====================================================== */}

        <TableContainer
          className={classes.tableContainer}
        >

          <Table
            stickyHeader
            className={classes.table}
          >

            <TableHead>

              <TableRow>

                <TableCell>
                  #
                </TableCell>


                <TableCell>
                  Invoice Group
                </TableCell>


                <TableCell>
                  Customer
                </TableCell>


                <TableCell>
                  Email
                </TableCell>


                <TableCell>
                  Lots
                </TableCell>


                <TableCell>
                  Artwork
                </TableCell>


                <TableCell>
                  Status
                </TableCell>


                <TableCell>
                  Error
                </TableCell>

              </TableRow>

            </TableHead>


            <TableBody>

              {safeRows.length === 0 ? (

                <TableRow>

                  <TableCell
                    colSpan={8}
                    className={classes.empty}
                  >

                    <Typography
                      color="textSecondary"
                    >
                      No invoice records found.
                    </Typography>

                  </TableCell>

                </TableRow>

              ) : (

                safeRows.map(
                  (row, index) => {

                    const lotNumbers =
                      Array.isArray(
                        row.lot_numbers
                      )
                        ? row.lot_numbers
                        : [];


                    const isValid =
                      row.status ===
                      "VALID";


                    return (

                      <TableRow
                        key={
                          `${row.invoice_group || "ROW"}-${index}`
                        }
                      >

                        {/* ROW NUMBER */}

                        <TableCell>
                          {
                            row.row_number ||
                            index + 2
                          }
                        </TableCell>


                        {/* INVOICE GROUP */}

                        <TableCell>

                          {row.invoice_group ||
                            "---"}

                        </TableCell>


                        {/* CUSTOMER */}

                        <TableCell>

                          {row.customer_name ||
                            "---"}

                        </TableCell>


                        {/* EMAIL */}

                        <TableCell>

                          {row.email ||
                            "---"}

                        </TableCell>


                        {/* LOTS */}

                        <TableCell>

                          <Typography
                            variant="body2"
                            className={
                              classes.lotText
                            }
                          >

                            {lotNumbers.length
                              ? lotNumbers.join(
                                  ", "
                                )
                              : "---"}

                          </Typography>

                        </TableCell>


                        {/* ARTWORK */}

                        <TableCell>

                          {row.include_artwork
                            ? "YES"
                            : "NO"}

                        </TableCell>


                        {/* STATUS */}

                        <TableCell>

                          {isValid ? (

                            <Box
                              display="flex"
                              alignItems="center"
                              className={
                                classes.valid
                              }
                            >

                              <CheckCircleIcon
                                fontSize="small"
                                style={{
                                  marginRight: 4,
                                }}
                              />

                              Valid

                            </Box>

                          ) : (

                            <Box
                              display="flex"
                              alignItems="center"
                              className={
                                classes.error
                              }
                            >

                              <ErrorIcon
                                fontSize="small"
                                style={{
                                  marginRight: 4,
                                }}
                              />

                              Error

                            </Box>

                          )}

                        </TableCell>


                        {/* ERROR */}

                        <TableCell>

                          {row.validation_error ||
                            ""}

                        </TableCell>

                      </TableRow>

                    );

                  }
                )

              )}

            </TableBody>

          </Table>

        </TableContainer>


        {/* ======================================================
            ACTIONS
        ====================================================== */}

        <Box className={classes.actions}>

          <Button
            className={classes.button}
            variant="outlined"
            onClick={onBack}
          >
            Back
          </Button>


          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            disabled={!canGenerate}
            onClick={onGenerate}
          >
            Generate{" "}
            {safeSummary.valid}{" "}
            Invoice
            {safeSummary.valid !== 1
              ? "s"
              : ""}
          </Button>

        </Box>


        {/* ======================================================
            ERROR MESSAGE
        ====================================================== */}

        {safeSummary.errors > 0 && (

          <Box mt={2}>

            <Typography
              color="error"
              variant="body2"
            >
              Please correct the Excel errors
              and upload the file again before
              generating invoices.
            </Typography>

          </Box>

        )}


        {/* ======================================================
            NO AUCTION
        ====================================================== */}

        {!selectedAuction && (

          <Box mt={2}>

            <Typography
              color="error"
              variant="body2"
            >
              No auction selected. Please go
              back to Step 1 and select an
              auction.
            </Typography>

          </Box>

        )}

      </CardContent>

    </Card>
  );
};


BulkInvoicePreview.defaultProps = {
  file: null,

  rows: [],

  selectedAuction: null,
};


BulkInvoicePreview.propTypes = {

  file:
    PropTypes.object,

  rows:
    PropTypes.array,

  summary:
    PropTypes.shape({

      total:
        PropTypes.number,

      valid:
        PropTypes.number,

      errors:
        PropTypes.number,

    }),

  selectedAuction:
    PropTypes.object,

  onBack:
    PropTypes.func.isRequired,

  onGenerate:
    PropTypes.func.isRequired,

};


export default BulkInvoicePreview;